import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { SentryErrorHandler } from '@sentry/angular';

import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { DialogWrapperService } from './dialog-wrapper.service';

@Injectable()
export class GlobalErrorHandler extends SentryErrorHandler {
  // Error handling is important and needs to be loaded first.
  // Because of this we should manually inject the services with Injector.

  constructor(private injector: Injector,
    private router: Router,
    private confirmationService: ConfirmationService,
    private zone: NgZone) {
    super();
  }

  async handleError(error: Error | HttpErrorResponse | any) {
    const dialogWrapperService = this.injector.get(DialogWrapperService);
    const location = this.injector.get(Location);
    // const logger = this.injector.get(ErrorLogger);
    // const ngZone = this.injector.get(NgZone);

    if (error.rejection) {
      // extract error from promise rejection
      error = error.rejection;
    }

    // A refusal is an answer, not a defect: no Sentry issue, and a dialog that does not offer to
    // leave the page. Tested before super.handleError so Sentry never sees it - which also skips
    // that method's console logging, hence the console.warn.
    //
    // The voucher collision is one refusal among the rest, handled here and nowhere else. Its own
    // toast says something better than the server's sentence, so it is checked first.
    if (this.isSaveRefused(error)) {
      console.warn('Save refused by the server:', error);
      if (this.isPhantomVoucherBlocked(error)) {
        this.showPhantomVoucherToast();
      } else {
        this.showSaveRefusedDialog(error);
      }
      return;
    }

    // A 401 means the session is gone, not that the code broke: logins live only in the server's
    // memory (MemoryCacheTicketStore), so every deploy or app restart signs everyone out at once,
    // and each open page's next call comes back 401. Reload to the login page without a Sentry
    // issue - before this check, each of those sign-outs was reported as "Error: 401: OK".
    if (this.is401(error)) {
      console.warn('Signed out by the server (401); reloading to the login page.', error);
      window.location.reload();
      return;
    }

    // Report via Sentry, and log to the console
    try {
      super.handleError(error);
    } catch (e) {
      console.error('Error in Sentry error handling', e);
    }

    // Doesn't seem to help with template error change detection infinite loop.
    // const debugCtx = error['ngDebugContext'];
    // const changeDetectorRef = debugCtx && debugCtx.injector.get(ChangeDetectorRef);
    // if (changeDetectorRef) {
    //   changeDetectorRef.detach();
    // }

    if (error instanceof HttpErrorResponse) {
      // Server Error
    } else {
      // Client Error
    }

    // Errors are logged by Sentry, above
    // logger.log(error.message);
    // console.error(error);

    await this.displayErrorDialog(error);
  }

  /**
   * A BeforeSaveEntities guard refused the save. Every guard in UniformaxPersistenceManager goes
   * through its ThrowException helper, which raises Breeze's EntityErrorsException, and
   * breeze-client puts the result on entityErrors. So the presence of that array is the whole
   * test - no matching on message text, and a guard added later needs no change here.
   *
   * This is Breeze's contract, not ours, and Proximity's isRecoverableServerError reads the same
   * field. An invented 422 status stood here first and was removed for that reason.
   *
   * 403 is the second handle, and it is not redundant. A guard that refuses before it looks at a
   * row - a type-level permission check - has no EntityInfo to pass, so its EntityErrors array
   * arrives empty and breeze-client may not surface entityErrors at all. Verified against
   * Breeze 7.5.0: GlobalExceptionFilter answers 403 with an ErrorDto for every
   * EntityErrorsException. Nothing else in this API returns 403, so the status is unambiguous.
   */
  private isSaveRefused(error: any): boolean {
    return error?.entityErrors != null
        || error?.error?.EntityErrors != null
        || error?.status === 403;
  }

  /**
   * The server's message is written for the user, so show it as the whole message and nothing
   * else. One button, and it closes: the edits that were refused are still on the screen and
   * still correctable, which is the entire difference from displayErrorDialog below.
   */
  private showSaveRefusedDialog(error: any) {
    // breeze-client puts the server's sentence on .message for a save error; the raw HTTP shape
    // keeps it on .error.Message. Both are the text the guard wrote for the user.
    const message = error?.message || error?.error?.Message || 'The server refused this save.';
    this.zone.run(() => {
      this.confirmationService.confirm({
        key: 'globalErrorDialog',
        header: 'Cannot save',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'OK',
        rejectVisible: false,
        message: message,
        accept: () => { /* stay on the page */ },
      });
    });
  }

  /**
   * Matches the server-side Rule 2 exception thrown by
   * UniformaxPersistenceManager.DetectPhantomVoucherShapes when a voucher would collide on
   * (JoHeaderId, ShipBinId).
   *
   * The 422 status already says "a guard refused this". This narrows that to WHICH guard, and
   * a verbatim message match is the only handle for that - there is one throw site, so it is
   * exact rather than merely adequate. Called only from inside the refusal branch.
   */
  private isPhantomVoucherBlocked(error: any): boolean {
    const msg = (error?.error?.Message || error?.message || '') as string;
    return msg.includes('a voucher already exists on JoHeader');
  }

  private showPhantomVoucherToast() {
    const toastr = this.injector.get(ToastrService);
    this.zone.run(() => {
      toastr.warning(
        'This bin has already been released. Reload the page to see the current state, then try again.',
        'Bin already released',
        { timeOut: 8000, closeButton: true },
      );
    });
  }

  async displayErrorDialog(error: any) {
    error = error.error || error;
    const errmsg = error.message || error.Message || error;
    const errorType = this.getErrorType(error);
    this.zone.run(() => {
      const errorTypeStr = errorType ? `${errorType}<br/>` : '';
      const msg = `An error has occurred, which may cause problems on the current page:<br/><br/>` + errorTypeStr + errmsg +
      `<br/><br/>Click OK to return to the home page.`;
      this.confirmationService.confirm({
        key: 'globalErrorDialog',
        header: "Error ",
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'OK',
        rejectLabel: 'Cancel',
        message: msg,
        accept: () => {
          this.router.navigateByUrl('/');
        },
      });
     });
  }

  /** Get type name to be used as dialog title for friendly, expected, or external errors */
  getErrorType(error: Error) {
    if (!error || !error.message) { return; }
    const message = error.message.toLowerCase();

    if (message.includes("java.io") || error.stack?.includes("_qz.")) {
      return this.getQzMessage(error);
    }
    if (message.includes("/shipping/") || message.includes("shipengine")) {
      return this.getShipMessage(error);
    }
    if (message.includes("/mail/")) {
      return this.getMailMessage(error);
    }
    if (message.includes("/quickbooks/")) {
      return this.getQuickbooksMessage(error);
    }
  }

  private is401(error: any): boolean {
    if (error instanceof HttpErrorResponse && error.status === 401) return true;
    if (error?.status === 401) return true;
    if (/\b401\b/.test(error?.message)) return true;
    return false;
  }

  innerMessage(error: Error | HttpErrorResponse | any) {
    const message = error.error?.Message || error.message;
    return message.substring(0, 500);
  }

  private getQzMessage(error: Error) {
    return { title: 'Printing Error', message:
    `Error from QZ Print Service: <p class="error-hint">${this.innerMessage(error)}</p> Please try again.` };
  }

  private getShipMessage(error: Error) {
    return { title: 'Shipping Service Error', message:
    `Error from Shipping Service: <p class="error-hint">${this.innerMessage(error)}</p> Please try again.` };
  }

  private getMailMessage(error: Error) {
    return { title: 'Mail Service Error', message:
    `Error from Mail Service: <p class="error-hint">${this.innerMessage(error)}</p> Please try again.` };
  }

  private getQuickbooksMessage(error: Error) {
    return { title: 'QuickBooks Error', message:
    `Error from QuickBooks Service: <p class="error-hint">${this.innerMessage(error)}</p> Please try again.` };
  }
}
